/**
 * How much someone is expected to work, and whether they have gone past it.
 *
 * Held in minutes like every other duration in the product, so nothing has to
 * convert at a boundary and no total is ever a float.
 *
 * Zero means unset, not "expected to work nothing". An unset target has no
 * opinion: nothing turns red, and no bar fills. Treating zero as a target would
 * put every person who has not filled this in permanently over it.
 */
export interface WorkTarget {
  /** Expected in a single day. */
  dailyMinutes: number;
  /** Expected across a week. Not necessarily five times the daily figure. */
  weeklyMinutes: number;
  /** Expected across a calendar month. */
  monthlyMinutes: number;
  /**
   * The days this person works, as JavaScript weekday numbers: 0 is Sunday,
   * 6 is Saturday. A freelancer on three days a week is the reason this is a
   * set rather than a count — which three matters to the capacity grid.
   */
  workDays: number[];
  /**
   * The window in which this person is available at all, as date keys. Null
   * at either end means open. Outside it every day counts as off, which is
   * how a contractor who finishes at the end of September stops being planned
   * for in October.
   */
  availableFrom: string | null;
  availableUntil: string | null;
  /** Billable share of tracked time this person aims for, 1–100. Zero means no target. */
  billablePercent: number;
}

/** Monday to Friday. */
export const DEFAULT_WORK_DAYS: readonly number[] = [1, 2, 3, 4, 5];

export const NO_WORK_TARGET: WorkTarget = {
  dailyMinutes: 0,
  weeklyMinutes: 0,
  monthlyMinutes: 0,
  workDays: [...DEFAULT_WORK_DAYS],
  availableFrom: null,
  availableUntil: null,
  billablePercent: 0,
};

/** A sensible starting point when someone first opens the field. */
export const SUGGESTED_WORK_TARGET: WorkTarget = {
  dailyMinutes: 8 * 60,
  weeklyMinutes: 40 * 60,
  monthlyMinutes: 160 * 60,
  workDays: [...DEFAULT_WORK_DAYS],
  availableFrom: null,
  availableUntil: null,
  billablePercent: 0,
};

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** Is this date key inside the person's availability window? */
export function isAvailableOn(
  target: Pick<WorkTarget, 'availableFrom' | 'availableUntil'>,
  dateKey: string,
): boolean {
  if (target.availableFrom && dateKey < target.availableFrom) return false;
  if (target.availableUntil && dateKey > target.availableUntil) return false;
  return true;
}

/** Does this person work on the weekday of this date key? */
export function worksOn(target: { workDays: readonly number[] }, dateKey: string): boolean {
  const weekday = new Date(`${dateKey}T00:00:00Z`).getUTCDay();
  return target.workDays.includes(weekday);
}

export function hasTarget(minutes: number): boolean {
  return Number.isFinite(minutes) && minutes > 0;
}

/**
 * Has this figure gone past its target?
 *
 * Strictly past. Exactly on target is on target — a full day that lands on
 * eight hours should not be flagged as an overrun, which is what `>=` would do
 * to almost everybody.
 */
export function isOver(minutes: number, targetMinutes: number): boolean {
  return hasTarget(targetMinutes) && minutes > targetMinutes;
}

/** By how much, or zero. Never negative. */
export function overBy(minutes: number, targetMinutes: number): number {
  return isOver(minutes, targetMinutes) ? minutes - targetMinutes : 0;
}

/**
 * Progress towards the target, from 0 to 1, for a bar.
 *
 * Capped, because a bar that renders 240% overflows its track. The overrun is
 * carried by colour and by the figure beside it, which do not have a maximum.
 */
export function progress(minutes: number, targetMinutes: number): number {
  if (!hasTarget(targetMinutes)) return 0;
  return Math.min(1, Math.max(0, minutes / targetMinutes));
}

/** The target that applies to a whole period of the timesheet. */
export function targetForPeriod(
  target: Pick<WorkTarget, 'weeklyMinutes' | 'monthlyMinutes'>,
  period: 'week' | 'month',
): number {
  return period === 'week' ? target.weeklyMinutes : target.monthlyMinutes;
}

/**
 * Fills in whatever is missing.
 *
 * Members created before targets existed have no field at all, and a member
 * document is read on every screen — so this has to cope with the absence
 * rather than the caller checking each time.
 */
export function resolveWorkTarget(stored: Partial<WorkTarget> | undefined | null): WorkTarget {
  const clean = (value: unknown): number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : 0;

  const days = Array.isArray(stored?.workDays)
    ? [...new Set(stored.workDays.filter((d): d is number => Number.isInteger(d) && d >= 0 && d <= 6))].sort(
        (a, b) => a - b,
      )
    : null;
  const dateOrNull = (value: unknown): string | null =>
    typeof value === 'string' && DATE_KEY.test(value) ? value : null;

  return {
    dailyMinutes: clean(stored?.dailyMinutes),
    weeklyMinutes: clean(stored?.weeklyMinutes),
    monthlyMinutes: clean(stored?.monthlyMinutes),
    // An absent field is "the usual week", not "no days at all".
    workDays: days === null ? [...DEFAULT_WORK_DAYS] : days,
    availableFrom: dateOrNull(stored?.availableFrom),
    availableUntil: dateOrNull(stored?.availableUntil),
    billablePercent: Math.min(100, clean(stored?.billablePercent)),
  };
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/**
 * "Mon–Fri", "Mon, Wed, Fri", or "No days". Runs of consecutive days are
 * collapsed, so the usual week reads as one word rather than five.
 */
export function formatWorkDays(workDays: readonly number[]): string {
  const days = [...new Set(workDays)].filter((d) => d >= 0 && d <= 6).sort((a, b) => a - b);
  if (days.length === 0) return 'No days';
  if (days.length === 7) return 'Every day';
  const runs: number[][] = [];
  for (const day of days) {
    const last = runs[runs.length - 1];
    if (last && last[last.length - 1] === day - 1) last.push(day);
    else runs.push([day]);
  }
  return runs
    .map((run) =>
      run.length >= 3
        ? `${WEEKDAY_SHORT[run[0]!]}–${WEEKDAY_SHORT[run[run.length - 1]!]}`
        : run.map((d) => WEEKDAY_SHORT[d]).join(', '),
    )
    .join(', ');
}
